import { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { currentLocaleTag } from '../../lib/formatting'
import {
  AlertCircle,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  List,
  Loader2,
  Pencil,
  Plus,
  Rocket,
  Save,
  Trash2,
  X,
} from 'lucide-react'
import { api, ApiError } from '../../lib/api'
import { confirmDialog } from '../../lib/dialogs'
import {
  ExecutionPhaseOrder,
  RoadmapItemStatusLabel,
  type ExecutionPhase,
  type RoadmapItemDto,
  type RoadmapItemStatus,
} from '@jr2os/shared'

interface Props {
  opportunityId: string
  executionPhase: ExecutionPhase | null
  canWrite: boolean
  onPhaseChanged?: () => void
}

const inputCls =
  'w-full px-2 py-1 rounded bg-[var(--bg-input-soft)] border border-base text-sm t-primary placeholder:t-faint focus:outline-none focus:border-jr2-500/50'

const btnPrimary =
  'px-2.5 py-1.5 rounded-lg text-xs font-semibold text-white bg-jr2-600 hover:bg-jr2-700 hover:shadow-glow-jr2 flex items-center gap-1.5'
const btnSecondary =
  'px-2.5 py-1.5 rounded-lg text-xs border border-base hover:bg-hover t-secondary flex items-center gap-1.5'

/** Estilos del punto/línea del timeline y badge por estado. */
const STATUS_STYLES: Record<RoadmapItemStatus, { dot: string; badge: string }> = {
  PENDIENTE: { dot: 'bg-slate-500', badge: 'border-slate-500/30 text-slate-600 dark:text-slate-300 bg-slate-500/10' },
  EN_CURSO: { dot: 'bg-jr2-500', badge: 'border-jr2-500/40 text-jr2-700 dark:text-jr2-200 bg-jr2-500/15' },
  COMPLETADO: {
    dot: 'bg-emerald-500',
    badge: 'border-emerald-500/40 text-emerald-700 dark:text-emerald-300 bg-emerald-500/10',
  },
  BLOQUEADO: { dot: 'bg-rose-500', badge: 'border-rose-500/40 text-rose-700 dark:text-rose-200 bg-rose-500/15' },
}

/** Estilos de la barra del Gantt por estado. */
const GANTT_BAR_STYLES: Record<RoadmapItemStatus, string> = {
  PENDIENTE: 'bg-slate-500/40 border-slate-400 text-slate-800 dark:text-slate-100',
  EN_CURSO: 'bg-jr2-500/40 border-jr2-300 text-jr2-900 dark:text-jr2-50',
  COMPLETADO: 'bg-emerald-500/40 border-emerald-300 text-emerald-900 dark:text-emerald-50',
  BLOQUEADO: 'bg-rose-500/40 border-rose-300 text-rose-900 dark:text-rose-50',
}

const MONTH_KEYS = ['jan', 'feb', 'mar', 'apr', 'may', 'jun', 'jul', 'aug', 'sep', 'oct', 'nov', 'dec'] as const

/**
 * Devuelve { leftPct, widthPct } recortando al año seleccionado, o null si la barra
 * cae completamente fuera del año o si falta alguna fecha.
 *
 * Mes 1 (1 ene) = 0%, fin del año (31 dic 23:59) = 100%.
 */
export function roadmapBarPosition(
  plannedStart: string | null,
  plannedEnd: string | null,
  year: number
): { leftPct: number; widthPct: number } | null {
  if (!plannedStart || !plannedEnd) return null
  const start = new Date(plannedStart).getTime()
  const end = new Date(plannedEnd).getTime()
  if (Number.isNaN(start) || Number.isNaN(end)) return null
  // Defensa contra datos corruptos: si end < start, no renderizamos barra
  // (en lugar de mostrar un sliver degenerado en una posición confusa).
  if (end < start) return null
  // El "fin" lo tratamos como inclusivo: empujamos un día para que un hito de un único día tenga ancho > 0.
  const endInclusive = end + 24 * 60 * 60 * 1000 - 1
  const yearStart = new Date(year, 0, 1).getTime()
  const yearEnd = new Date(year + 1, 0, 1).getTime() - 1
  // Fuera del año por completo.
  if (endInclusive < yearStart || start > yearEnd) return null
  const visStart = Math.max(start, yearStart)
  const visEnd = Math.min(endInclusive, yearEnd)
  const yearSpan = yearEnd - yearStart
  const leftPct = ((visStart - yearStart) / yearSpan) * 100
  const widthPct = ((visEnd - visStart) / yearSpan) * 100
  return { leftPct, widthPct: Math.max(widthPct, 0.5) }
}

interface ItemForm {
  title: string
  phase: ExecutionPhase | ''
  status: RoadmapItemStatus
  plannedStart: string
  plannedEnd: string
}

const emptyForm: ItemForm = {
  title: '',
  phase: '',
  status: 'PENDIENTE',
  plannedStart: '',
  plannedEnd: '',
}

/** YYYY-MM-DD -> ISO datetime (mediodía local) o null si vacío. */
function dateToIso(date: string): string | null {
  if (!date) return null
  return new Date(`${date}T12:00:00`).toISOString()
}

/** ISO datetime -> YYYY-MM-DD para <input type="date"> (o '' si null). */
function isoToDate(iso: string | null): string {
  if (!iso) return ''
  return new Date(iso).toISOString().slice(0, 10)
}

function fmtDate(iso: string): string {
  return new Date(iso).toLocaleDateString(currentLocaleTag())
}

export default function ExecutionPanel({
  opportunityId,
  executionPhase,
  canWrite,
  onPhaseChanged,
}: Props) {
  const { t } = useTranslation(['opportunities', 'common'])
  const [items, setItems] = useState<RoadmapItemDto[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [phaseSaving, setPhaseSaving] = useState<ExecutionPhase | null>(null)
  const [adding, setAdding] = useState(false)
  const [editingId, setEditingId] = useState<string | null>(null)
  const [form, setForm] = useState<ItemForm>(emptyForm)
  const [formError, setFormError] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)
  const [view, setView] = useState<'list' | 'gantt'>('list')
  const [ganttYear, setGanttYear] = useState<number>(new Date().getFullYear())

  const reload = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const resp = await api.get<{ items: RoadmapItemDto[] }>(
        `/opportunities/${opportunityId}/roadmap`
      )
      setItems(resp.items)
    } catch (err) {
      setError(err instanceof Error ? err.message : t('execution.errors.loadRoadmap'))
    } finally {
      setLoading(false)
    }
  }, [opportunityId])

  useEffect(() => {
    void reload()
  }, [reload])

  const onPickPhase = async (phase: ExecutionPhase) => {
    if (!canWrite || phase === executionPhase) return
    setPhaseSaving(phase)
    setError(null)
    try {
      await api.patch(`/opportunities/${opportunityId}`, { executionPhase: phase })
      // Reajuste al entregar: al marcar FINALIZADO, si hay igualas de
      // mantenimiento aún no arrancadas, ofrecemos anclar su inicio a la fecha de
      // entrega (que el backend acaba de sellar). Comodidad opcional: si algo
      // falla, no bloquea el cambio de fase.
      if (phase === 'FINALIZADO') {
        try {
          const resp = await api.get<{ items: { generatedCount: number }[] }>(
            `/recurring-invoices?opportunityId=${opportunityId}&status=ACTIVE`,
          )
          const pending = resp.items.filter((r) => r.generatedCount === 0)
          if (
            pending.length > 0 &&
            (await confirmDialog(t('execution.reanchor.prompt', { count: pending.length })))
          ) {
            await api.post(`/opportunities/${opportunityId}/maintenance/reanchor`, {})
          }
        } catch {
          /* reanclaje best-effort */
        }
      }
      onPhaseChanged?.()
    } catch (err) {
      const body = err instanceof ApiError ? (err.body as { error?: string }) : undefined
      setError(body?.error ?? (err instanceof Error ? err.message : t('execution.errors.changePhase')))
    } finally {
      setPhaseSaving(null)
    }
  }

  const openAdd = () => {
    setForm(emptyForm)
    setFormError(null)
    setEditingId(null)
    setAdding(true)
  }

  const openEdit = (item: RoadmapItemDto) => {
    setForm({
      title: item.title,
      phase: item.phase ?? '',
      status: item.status,
      plannedStart: isoToDate(item.plannedStart),
      plannedEnd: isoToDate(item.plannedEnd),
    })
    setFormError(null)
    setAdding(false)
    setEditingId(item.id)
  }

  const closeForm = () => {
    setAdding(false)
    setEditingId(null)
    setFormError(null)
  }

  const submitForm = async () => {
    if (!form.title.trim()) {
      setFormError(t('execution.errors.titleRequired'))
      return
    }
    setSubmitting(true)
    setFormError(null)
    const body = {
      title: form.title.trim(),
      phase: form.phase || null,
      status: form.status,
      plannedStart: dateToIso(form.plannedStart),
      plannedEnd: dateToIso(form.plannedEnd),
    }
    try {
      if (editingId) {
        await api.patch(`/opportunities/${opportunityId}/roadmap/${editingId}`, body)
      } else {
        await api.post(`/opportunities/${opportunityId}/roadmap`, body)
      }
      closeForm()
      await reload()
    } catch (err) {
      const eb = err instanceof ApiError ? (err.body as { error?: string }) : undefined
      setFormError(eb?.error ?? (err instanceof Error ? err.message : t('execution.errors.save')))
    } finally {
      setSubmitting(false)
    }
  }

  const onChangeStatus = async (item: RoadmapItemDto, status: RoadmapItemStatus) => {
    try {
      await api.patch(`/opportunities/${opportunityId}/roadmap/${item.id}`, { status })
      await reload()
    } catch (err) {
      const body = err instanceof ApiError ? (err.body as { error?: string }) : undefined
      setError(body?.error ?? (err instanceof Error ? err.message : 'Error'))
    }
  }

  const onDelete = async (item: RoadmapItemDto) => {
    if (
      !(await confirmDialog({
        message: t('execution.confirmDeleteItem', { title: item.title }),
        tone: 'danger',
      }))
    )
      return
    try {
      await api.delete(`/opportunities/${opportunityId}/roadmap/${item.id}`)
      await reload()
    } catch (err) {
      const body = err instanceof ApiError ? (err.body as { error?: string }) : undefined
      setError(body?.error ?? (err instanceof Error ? err.message : 'Error'))
    }
  }

  const currentIdx = executionPhase ? ExecutionPhaseOrder.indexOf(executionPhase) : -1

  return (
    <div className="surface p-4 space-y-3">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <h3 className="t-primary font-semibold text-sm flex items-center gap-2">
          <Rocket size={14} className="text-jr2-700 dark:text-jr2-300" />
          {t('execution.title')}
        </h3>
        {/* CTA explícito: los círculos del stepper también cambian de fase,
            pero no parecen clicables — este botón es la afordancia. */}
        {canWrite &&
          executionPhase != null &&
          currentIdx >= 0 &&
          currentIdx < ExecutionPhaseOrder.length - 1 && (
            <button
              type="button"
              onClick={() => void onPickPhase(ExecutionPhaseOrder[currentIdx + 1]!)}
              disabled={phaseSaving != null}
              className="inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-lg border border-jr2-500/40 text-jr2-700 dark:text-jr2-200 hover:bg-jr2-500/10 disabled:opacity-50 transition"
            >
              {phaseSaving != null ? (
                <Loader2 size={12} className="animate-spin" />
              ) : (
                <ChevronRight size={12} />
              )}
              {t('execution.advanceTo', {
                phase: t('enums:ExecutionPhase.' + ExecutionPhaseOrder[currentIdx + 1]!),
              })}
            </button>
          )}
      </div>

      {error && (
        <div className="text-xs text-rose-700 dark:text-rose-300 bg-rose-500/10 border border-rose-500/30 rounded-lg px-2.5 py-1.5 flex items-center gap-2">
          <AlertCircle size={12} /> {error}
        </div>
      )}

      {/* STEPPER de fase */}
      {executionPhase == null ? (
        <p className="text-xs t-faint">{t('execution.notInExecution')}</p>
      ) : (
        <div className="flex items-center">
          {ExecutionPhaseOrder.map((phase, idx) => {
            const isCurrent = phase === executionPhase
            const isDone = idx < currentIdx
            const isBusy = phaseSaving === phase
            const stepCls = isCurrent
              ? 'border-jr2-500 bg-jr2-500/20 text-jr2-700 dark:text-jr2-200'
              : isDone
                ? 'border-emerald-500/50 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300'
                : 'border-base t-faint'
            const StepInner = (
              <span
                className={`w-7 h-7 grid place-items-center rounded-full border text-[11px] font-semibold ${stepCls} ${
                  canWrite && !isCurrent ? 'hover:border-jr2-500/60' : ''
                }`}
              >
                {isBusy ? (
                  <Loader2 size={12} className="animate-spin" />
                ) : isDone ? (
                  <CheckCircle2 size={14} />
                ) : (
                  idx + 1
                )}
              </span>
            )
            return (
              <div key={phase} className="flex items-center flex-1 last:flex-none">
                <div className="flex flex-col items-center gap-1">
                  {canWrite ? (
                    <button
                      type="button"
                      onClick={() => void onPickPhase(phase)}
                      disabled={isBusy || isCurrent}
                      title={t('execution.markPhase', { phase: t('enums:ExecutionPhase.' + phase) })}
                      className="disabled:cursor-default"
                    >
                      {StepInner}
                    </button>
                  ) : (
                    StepInner
                  )}
                  <span
                    className={`text-[10px] ${isCurrent ? 'text-jr2-700 dark:text-jr2-200 font-semibold' : 't-faint'}`}
                  >
                    {t('enums:ExecutionPhase.' + phase)}
                  </span>
                </div>
                {idx < ExecutionPhaseOrder.length - 1 && (
                  <div
                    className={`flex-1 h-px mx-1 mb-4 ${idx < currentIdx ? 'bg-emerald-500/40' : 'bg-base'}`}
                  />
                )}
              </div>
            )
          })}
        </div>
      )}

      {/* ROADMAP */}
      <div className="pt-1">
        <div className="flex items-center justify-between mb-2 gap-2 flex-wrap">
          <h4 className="t-secondary text-xs font-semibold uppercase tracking-wider">
            {t('execution.roadmapTitle')}
          </h4>
          <div className="flex items-center gap-2">
            {/* Toggle Lista | Gantt */}
            <div className="inline-flex rounded-lg border border-base overflow-hidden">
              <button
                type="button"
                onClick={() => setView('list')}
                className={`px-2 py-1 text-[11px] flex items-center gap-1 ${
                  view === 'list'
                    ? 'bg-jr2-500/20 text-jr2-700 dark:text-jr2-200'
                    : 't-faint hover:bg-hover'
                }`}
                title={t('execution.listView')}
              >
                <List size={11} /> {t('execution.list')}
              </button>
              <button
                type="button"
                onClick={() => setView('gantt')}
                className={`px-2 py-1 text-[11px] flex items-center gap-1 border-l border-base ${
                  view === 'gantt'
                    ? 'bg-jr2-500/20 text-jr2-700 dark:text-jr2-200'
                    : 't-faint hover:bg-hover'
                }`}
                title={t('execution.ganttView')}
              >
                <Rocket size={11} /> {t('execution.ganttLabel')}
              </button>
            </div>
            {canWrite && !adding && editingId == null && (
              <button type="button" onClick={openAdd} className={btnPrimary}>
                <Plus size={12} /> {t('execution.addMilestone')}
              </button>
            )}
          </div>
        </div>

        {adding && editingId == null && (
          <RoadmapForm
            form={form}
            setForm={setForm}
            error={formError}
            submitting={submitting}
            onSubmit={submitForm}
            onCancel={closeForm}
            isEdit={false}
          />
        )}

        {loading ? (
          <div className="t-faint text-xs flex items-center gap-1.5">
            <Loader2 size={11} className="animate-spin" /> {t('common:loading')}
          </div>
        ) : items.length === 0 && !adding ? (
          <p className="text-xs t-faint">{t('execution.noMilestones')}</p>
        ) : view === 'gantt' ? (
          <RoadmapGanttView
            items={items}
            year={ganttYear}
            onYearChange={setGanttYear}
            onEditItem={canWrite ? openEdit : undefined}
          />
        ) : (
          <ul className="space-y-0">
            {items.map((item, idx) => {
              const styles = STATUS_STYLES[item.status]
              const isLast = idx === items.length - 1
              if (editingId === item.id) {
                return (
                  <li key={item.id} className="py-2">
                    <RoadmapForm
                      form={form}
                      setForm={setForm}
                      error={formError}
                      submitting={submitting}
                      onSubmit={submitForm}
                      onCancel={closeForm}
                      isEdit
                    />
                  </li>
                )
              }
              return (
                <li key={item.id} className="flex gap-3">
                  {/* punto + línea timeline */}
                  <div className="flex flex-col items-center pt-1.5">
                    <span className={`w-2.5 h-2.5 rounded-full ${styles.dot}`} />
                    {!isLast && <span className="w-px flex-1 bg-base mt-1" />}
                  </div>
                  <div className="flex-1 min-w-0 pb-3">
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <div className="t-primary text-sm font-medium">{item.title}</div>
                        {item.description && (
                          <p className="t-muted text-xs mt-0.5">{item.description}</p>
                        )}
                        <div className="flex items-center gap-1.5 flex-wrap mt-1">
                          <span
                            className={`px-1.5 py-0.5 rounded border text-[10px] ${styles.badge}`}
                          >
                            {t('enums:RoadmapItemStatus.' + item.status)}
                          </span>
                          {item.phase != null && (
                            <span className="px-1.5 py-0.5 rounded border border-base text-[10px] t-secondary">
                              {t('enums:ExecutionPhase.' + item.phase)}
                            </span>
                          )}
                          {(item.plannedStart || item.plannedEnd) && (
                            <span className="text-[10px] t-faint">
                              {item.plannedStart ? fmtDate(item.plannedStart) : '—'}
                              {' – '}
                              {item.plannedEnd ? fmtDate(item.plannedEnd) : '—'}
                            </span>
                          )}
                        </div>
                      </div>
                      {canWrite && (
                        <div className="flex items-center gap-1 shrink-0">
                          <select
                            value={item.status}
                            onChange={(e) =>
                              void onChangeStatus(item, e.target.value as RoadmapItemStatus)
                            }
                            className="px-1.5 py-1 rounded bg-[var(--bg-input-soft)] border border-base text-[11px] t-secondary focus:outline-none focus:border-jr2-500/50"
                            title={t('execution.changeStatus')}
                          >
                            {(Object.keys(RoadmapItemStatusLabel) as RoadmapItemStatus[]).map(
                              (s) => (
                                <option key={s} value={s}>
                                  {t('enums:RoadmapItemStatus.' + s)}
                                </option>
                              )
                            )}
                          </select>
                          <button
                            type="button"
                            onClick={() => openEdit(item)}
                            className="w-6 h-6 grid place-items-center rounded hover:bg-hover t-muted hover:text-jr2-700 dark:hover:text-jr2-200"
                            title={t('common:actions.edit')}
                          >
                            <Pencil size={11} />
                          </button>
                          <button
                            type="button"
                            onClick={() => void onDelete(item)}
                            className="w-6 h-6 grid place-items-center rounded hover:bg-rose-500/10 t-muted hover:text-rose-400"
                            title={t('common:actions.delete')}
                          >
                            <Trash2 size={11} />
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                </li>
              )
            })}
          </ul>
        )}
      </div>
    </div>
  )
}

function RoadmapForm({
  form,
  setForm,
  error,
  submitting,
  onSubmit,
  onCancel,
  isEdit,
}: {
  form: ItemForm
  setForm: React.Dispatch<React.SetStateAction<ItemForm>>
  error: string | null
  submitting: boolean
  onSubmit: () => void
  onCancel: () => void
  isEdit: boolean
}) {
  const { t } = useTranslation(['opportunities', 'common'])
  const set = <K extends keyof ItemForm>(k: K, v: ItemForm[K]) =>
    setForm((f) => ({ ...f, [k]: v }))

  return (
    <div className="surface p-3 space-y-2 mb-3 border border-jr2-500/20">
      {error && (
        <div className="text-xs text-rose-700 dark:text-rose-300 bg-rose-500/10 border border-rose-500/30 rounded-lg px-2.5 py-1.5 flex items-center gap-2">
          <AlertCircle size={12} /> {error}
        </div>
      )}
      <div>
        <label className="block text-[11px] t-muted mb-1">{t('execution.form.title')}</label>
        <input
          value={form.title}
          onChange={(e) => set('title', e.target.value)}
          placeholder={t('execution.form.titlePlaceholder')}
          className={inputCls}
        />
      </div>
      <div className="grid grid-cols-2 gap-2">
        <div>
          <label className="block text-[11px] t-muted mb-1">{t('execution.form.phase')}</label>
          <select
            value={form.phase}
            onChange={(e) => set('phase', e.target.value as ExecutionPhase | '')}
            className={inputCls}
          >
            <option value="">{t('execution.form.noPhase')}</option>
            {ExecutionPhaseOrder.map((p) => (
              <option key={p} value={p}>
                {t('enums:ExecutionPhase.' + p)}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-[11px] t-muted mb-1">{t('execution.form.status')}</label>
          <select
            value={form.status}
            onChange={(e) => set('status', e.target.value as RoadmapItemStatus)}
            className={inputCls}
          >
            {(Object.keys(RoadmapItemStatusLabel) as RoadmapItemStatus[]).map((s) => (
              <option key={s} value={s}>
                {t('enums:RoadmapItemStatus.' + s)}
              </option>
            ))}
          </select>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-2">
        <div>
          <label className="block text-[11px] t-muted mb-1">{t('execution.form.plannedStart')}</label>
          <input
            type="date"
            value={form.plannedStart}
            onChange={(e) => set('plannedStart', e.target.value)}
            className={inputCls}
          />
        </div>
        <div>
          <label className="block text-[11px] t-muted mb-1">{t('execution.form.plannedEnd')}</label>
          <input
            type="date"
            value={form.plannedEnd}
            onChange={(e) => set('plannedEnd', e.target.value)}
            className={inputCls}
          />
        </div>
      </div>
      <div className="flex items-center gap-2 justify-end">
        <button type="button" onClick={onCancel} className={btnSecondary}>
          <X size={12} /> {t('common:actions.cancel')}
        </button>
        <button type="button" onClick={onSubmit} disabled={submitting} className={btnPrimary}>
          {submitting ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />}
          {isEdit ? t('common:actions.save') : t('common:actions.add')}
        </button>
      </div>
    </div>
  )
}

/* -------------------------------------------------------------------------- */
/* Vista Gantt                                                                 */
/* -------------------------------------------------------------------------- */

interface GanttProps {
  items: RoadmapItemDto[]
  year: number
  onYearChange: (year: number) => void
  onEditItem?: (item: RoadmapItemDto) => void
}

function RoadmapGanttView({ items, year, onYearChange, onEditItem }: GanttProps) {
  const { t } = useTranslation('opportunities')
  /** Hitos sin fechas no se renderizan; los contamos para el aviso al pie. */
  const undatedCount = useMemo(
    () => items.filter((it) => !it.plannedStart || !it.plannedEnd).length,
    [items]
  )
  /** Hitos con fechas pero SIN phase: no caben en ninguna fila. Los contamos aparte. */
  const noPhaseCount = useMemo(
    () =>
      items.filter(
        (it) => it.plannedStart && it.plannedEnd && !it.phase,
      ).length,
    [items],
  )

  /** Agrupa por phase. Los items sin phase quedan invisibles (los avisamos en el footer). */
  const itemsByPhase = useMemo(() => {
    const map = new Map<ExecutionPhase, RoadmapItemDto[]>()
    for (const phase of ExecutionPhaseOrder) map.set(phase, [])
    for (const it of items) {
      if (it.phase && map.has(it.phase)) {
        map.get(it.phase)!.push(it)
      }
    }
    return map
  }, [items])

  return (
    <div className="space-y-2">
      {/* Selector de año */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={() => onYearChange(year - 1)}
            className="w-6 h-6 grid place-items-center rounded hover:bg-hover t-muted hover:text-jr2-700 dark:hover:text-jr2-200 border border-base"
            title={t('execution.gantt.prevYear')}
          >
            <ChevronLeft size={12} />
          </button>
          <div className="px-2 py-0.5 text-xs t-primary font-semibold tabular-nums min-w-[3.5rem] text-center">
            {year}
          </div>
          <button
            type="button"
            onClick={() => onYearChange(year + 1)}
            className="w-6 h-6 grid place-items-center rounded hover:bg-hover t-muted hover:text-jr2-700 dark:hover:text-jr2-200 border border-base"
            title={t('execution.gantt.nextYear')}
          >
            <ChevronRight size={12} />
          </button>
        </div>
        <button
          type="button"
          onClick={() => onYearChange(new Date().getFullYear())}
          className="text-[10px] t-faint hover:text-jr2-700 dark:hover:text-jr2-200"
          title={t('execution.gantt.backToCurrentYear')}
        >
          {t('execution.gantt.today')}
        </button>
      </div>

      {/* Cabecera de meses con quarters */}
      <div className="border border-base rounded-lg overflow-hidden">
        <div className="grid grid-cols-[10rem_1fr] text-[10px]">
          <div className="bg-[var(--bg-input-soft)] border-r border-base p-1" />
          <div>
            {/* Quarters */}
            <div className="grid grid-cols-4 border-b border-base">
              {['Q1', 'Q2', 'Q3', 'Q4'].map((q, idx) => (
                <div
                  key={q}
                  className={`px-1 py-0.5 text-center t-faint font-semibold uppercase tracking-wider ${
                    idx < 3 ? 'border-r border-base' : ''
                  }`}
                >
                  {q}
                </div>
              ))}
            </div>
            {/* Meses */}
            <div className="grid grid-cols-12">
              {MONTH_KEYS.map((m, idx) => (
                <div
                  key={m}
                  className={`px-0.5 py-0.5 text-center t-muted ${
                    idx < 11 ? 'border-r border-base/50' : ''
                  } ${idx % 3 === 2 && idx < 11 ? 'border-r border-base' : ''}`}
                >
                  {t(`execution.gantt.months.${m}`)}
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Filas de phases */}
        {ExecutionPhaseOrder.map((phase) => {
          const phaseItems = itemsByPhase.get(phase) ?? []
          const visibleItems = phaseItems.filter(
            (it) => roadmapBarPosition(it.plannedStart, it.plannedEnd, year) !== null
          )
          return (
            <div
              key={phase}
              className="grid grid-cols-[10rem_1fr] border-t border-base"
            >
              {/* Etiqueta de phase */}
              <div className="bg-jr2-500/10 border-r border-base px-2 py-2 flex items-center">
                <span className="text-[11px] font-semibold text-jr2-700 dark:text-jr2-200">
                  {t('enums:ExecutionPhase.' + phase)}
                </span>
              </div>
              {/* Track con gridlines de meses */}
              <div className="relative min-h-[3rem] py-1">
                {/* Gridlines verticales (meses) */}
                <div className="absolute inset-0 grid grid-cols-12 pointer-events-none">
                  {MONTH_KEYS.map((_, idx) => (
                    <div
                      key={idx}
                      className={`${idx < 11 ? 'border-r border-base/30' : ''} ${
                        idx % 3 === 2 && idx < 11 ? 'border-r border-base/60' : ''
                      }`}
                    />
                  ))}
                </div>
                {visibleItems.length === 0 ? (
                  <div className="relative h-8 grid place-items-center">
                    <span className="t-faint text-[10px]">—</span>
                  </div>
                ) : (
                  <div className="relative space-y-1">
                    {visibleItems.map((it) => {
                      const pos = roadmapBarPosition(it.plannedStart, it.plannedEnd, year)!
                      const cls = GANTT_BAR_STYLES[it.status]
                      const tooltip = `${it.title}\n${
                        it.plannedStart ? fmtDate(it.plannedStart) : '—'
                      } – ${it.plannedEnd ? fmtDate(it.plannedEnd) : '—'}\n${
                        t('enums:RoadmapItemStatus.' + it.status)
                      }`
                      return (
                        <div key={it.id} className="relative h-6">
                          <button
                            type="button"
                            onClick={() => onEditItem?.(it)}
                            disabled={!onEditItem}
                            title={tooltip}
                            style={{
                              left: `${pos.leftPct}%`,
                              width: `${pos.widthPct}%`,
                            }}
                            className={`absolute top-0 h-6 rounded border ${cls} text-[10px] px-1.5 truncate flex items-center ${
                              onEditItem
                                ? 'cursor-pointer hover:brightness-125'
                                : 'cursor-default'
                            }`}
                          >
                            <span className="truncate">{it.title}</span>
                          </button>
                        </div>
                      )
                    })}
                  </div>
                )}
              </div>
            </div>
          )
        })}
      </div>

      {(undatedCount > 0 || noPhaseCount > 0) && (
        <p className="text-[10px] t-faint">
          {undatedCount > 0 && (
            <>{t('execution.gantt.undatedCount', { count: undatedCount })}</>
          )}
          {undatedCount > 0 && noPhaseCount > 0 && ' · '}
          {noPhaseCount > 0 && (
            <>{t('execution.gantt.noPhaseCount', { count: noPhaseCount })}</>
          )}
          {' '}
          {t('execution.gantt.notShownSuffix', { count: undatedCount + noPhaseCount })}
        </p>
      )}
    </div>
  )
}
